/**
 * MY WISHLIST PAGE
 * 
 * Unified page showing everything the user wants/is watching:
 * - Items I'm Seeking (ISOs/DISOs/Unicorns from personal wishlist)
 * - Destash I'm Watching (marketplace items from other sellers)
 * - Creations I'm Watching (handmade items like press-ons, jewelry)
 * 
 * Route: /my-wants
 * 
 * UPDATED: Glassmorphic styling, custom filters per section
 * UPDATED: Collapsible category sections for ISO items
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  collection, getDocs, doc, getDoc, deleteDoc, 
  orderBy, query, updateDoc, serverTimestamp 
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Search, Star, Trash2, MessageSquare, ExternalLink, Tag,
  Repeat, AlertCircle, Loader2, ArrowLeft,
  Sparkles, Package, Check, X, ChevronDown, ChevronUp
} from 'lucide-react';
import InquiryModal from '../messaging/components/InquiryModal';
import PolishDetailModal from '../components/polish/PolishDetailModal';
import { PriorityColors } from '../constants/categoryColors';
import { PageHeader } from '../components/common';
import PlateDetailModal from '../components/plates/PlateDetailModal';
import DecalDetailModal from '../components/decals/DecalDetailModal';
import EmbellishmentDetailModal from '../components/embellishments/EmbellishmentDetailModal';
import ToolDetailModal from '../components/tools/ToolDetailModal';
import BuffingPowderDetailModal from '../components/powders/BuffingPowderDetailModal';
import AcrylicDetailModal from '../components/acrylics/AcrylicDetailModal';
import PageVisibilityBanner from '../components/PageVisibilityBanner';
import type { CollectionItem } from '../types';
import type { AttachedItem } from '../messaging/types';

// ============================================================================
// TYPES
// ============================================================================

// ISOItem is just a CollectionItem - all fields come from the base type
type ISOItem = CollectionItem;

interface WatchingDestashItem {
  id: string;
  itemId: string;
  sellerId: string;
  itemName: string;
  itemBrand: string;
  itemType: string;
  photoUrl: string | null;
  price: number | null;
  addedAt: any;
  currentStatus?: 'available' | 'pending' | 'sold' | 'removed';
  currentPrice?: number;
  isOpenToSwaps?: boolean;
  sellerUsername?: string;
  sellerAvatar?: string;
}

interface WatchingCreationItem {
  id: string;
  creationId: string;
  creatorId: string;
  title: string;
  photoUrl: string | null;
  price: number | null;
  marketplaceCategory?: string;
  addedAt: any;
  currentStatus?: 'available' | 'pending' | 'sold' | 'removed';
  creatorUsername?: string;
  creatorAvatar?: string;
}

type ActiveTab = 'seeking' | 'destash' | 'creations';
type PriorityFilter = 'all' | 'iso' | 'diso' | 'unicorn';
type CategoryFilter = 'all' | 'pressons' | 'jewelry' | 'other';

// ============================================================================
// HELPER: Get display info for item types
// ============================================================================

const getItemTypeDisplay = (itemType: string): { label: string; emoji: string } => {
  const typeMap: Record<string, { label: string; emoji: string }> = {
    'polish': { label: 'Nail Polish', emoji: '💅' },
    'plate': { label: 'Stamping Plates', emoji: '🔲' },
    'transfer': { label: 'Transfers & Decals', emoji: '🦋' },
    'embellishment': { label: 'Embellishments', emoji: '💎' },
    'tool': { label: 'Tools', emoji: '🛠️' },
    'powder': { label: 'Buffing Powders', emoji: '✨' },
    'acrylic': { label: 'Acrylics & Builders', emoji: '💪' },
  };
  
  // Return mapped value or create a nice fallback for unknown types
  return typeMap[itemType] || { 
    label: itemType.charAt(0).toUpperCase() + itemType.slice(1), 
    emoji: '📦' 
  };
};

// ============================================================================
// COMPONENT
// ============================================================================

const Wishlist: React.FC = () => {
  const navigate = useNavigate();
  
  // Auth state
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Data state
  const [isoItems, setIsoItems] = useState<ISOItem[]>([]);
  const [destashItems, setDestashItems] = useState<WatchingDestashItem[]>([]);
  const [creationItems, setCreationItems] = useState<WatchingCreationItem[]>([]);
  
  // Loading states for each section
  const [loadingISO, setLoadingISO] = useState(true);
  const [loadingDestash, setLoadingDestash] = useState(true);
  const [loadingCreations, setLoadingCreations] = useState(true);
  
  // UI state
  const [activeTab, setActiveTab] = useState<ActiveTab>('seeking');
  const [searchQuery, setSearchQuery] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);
  
  // Filter states
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  
  // Collapsible section state - track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  
  // Pagination state (for destash and creations tabs)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Per-category pagination state for ISO items
  const [categoryPages, setCategoryPages] = useState<Record<string, number>>({});
  const [isoItemsPerPage, setIsoItemsPerPage] = useState(20);
  
  // Inquiry modal state
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryRecipientId, setInquiryRecipientId] = useState<string | null>(null);
  const [inquiryRecipientUsername, setInquiryRecipientUsername] = useState<string | null>(null);
  const [inquiryItems, setInquiryItems] = useState<AttachedItem[]>([]);

  // Item detail modal state
  const [selectedItem, setSelectedItem] = useState<ISOItem | null>(null);

  // User profile for visibility banner
  const [username, setUsername] = useState<string>('');
  const [isWishlistPublic, setIsWishlistPublic] = useState<boolean>(false);

  // ============================================================================
  // AUTH & DATA LOADING
  // ============================================================================

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUserId(user?.uid || null);
      if (user) {
        loadAllData(user.uid);
        
        // Fetch user profile for visibility banner
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUsername(data.username || data.profile?.username || '');
            setIsWishlistPublic(data.privacy?.showWishlist !== false);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const loadAllData = async (userId: string) => {
    setLoading(true);
    await Promise.all([
      loadISOItems(userId),
      loadDestashItems(userId),
      loadCreationItems(userId)
    ]);
    setLoading(false);
  };

  // Load ISO/PublicWishlist items
  const loadISOItems = async (userId: string) => {
    setLoadingISO(true);
    try {
      const itemsRef = collection(db, 'users', userId, 'items');
      const snapshot = await getDocs(itemsRef);
      
      const items: ISOItem[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.onWishlist && !data.isDestashed) {
          items.push({
            id: docSnap.id,
            ...data,
            dateAdded: data.dateAdded?.toDate() || data.createdAt?.toDate() || new Date(),
          } as ISOItem);
        }
      });

      // Sort by priority (unicorn first)
      items.sort((a, b) => {
        const priorityOrder = { unicorn: 0, diso: 1, iso: 2 };
        const aPriority = priorityOrder[a.isoPriority || 'iso'] ?? 2;
        const bPriority = priorityOrder[b.isoPriority || 'iso'] ?? 2;
        return aPriority - bPriority;
      });

      setIsoItems(items);
      
      // Auto-expand all categories that have items on first load
      const uniqueTypes = [...new Set(items.map(item => item.itemType || 'unknown'))];
      setExpandedCategories(new Set(uniqueTypes));
      
    } catch (err) {
      console.error('Error loading ISO items:', err);
    } finally {
      setLoadingISO(false);
    }
  };

  // Load Destash watching items
  const loadDestashItems = async (userId: string) => {
    setLoadingDestash(true);
    try {
      const watchingRef = collection(db, 'users', userId, 'marketplaceWishlist');
      const q = query(watchingRef, orderBy('addedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const items: WatchingDestashItem[] = [];
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const item: WatchingDestashItem = {
          id: docSnap.id,
          itemId: data.itemId,
          sellerId: data.sellerId,
          itemName: data.itemName,
          itemBrand: data.itemBrand,
          itemType: data.itemType,
          photoUrl: data.photoUrl,
          price: data.price,
          addedAt: data.addedAt,
        };

        // Fetch current status
        try {
          const itemRef = doc(db, 'users', data.sellerId, 'items', data.itemId);
          const itemDoc = await getDoc(itemRef);
          
          if (itemDoc.exists()) {
            const itemData = itemDoc.data();
            item.currentStatus = itemData.destashStatus || 'available';
            item.currentPrice = itemData.destashPrice;
            item.isOpenToSwaps = itemData.isOpenToSwaps;
          } else {
            item.currentStatus = 'removed';
          }
        } catch (err) {
          item.currentStatus = 'removed';
        }

        // Fetch seller info
        try {
          const sellerRef = doc(db, 'users', data.sellerId);
          const sellerDoc = await getDoc(sellerRef);
          
          if (sellerDoc.exists()) {
            const sellerData = sellerDoc.data();
            item.sellerUsername = sellerData.username || sellerData.profile?.username;
            item.sellerAvatar = sellerData.avatarUrl || sellerData.profile?.avatarUrl;
          }
        } catch (err) {
          console.error('Error fetching seller info:', err);
        }

        items.push(item);
      }

      setDestashItems(items);
    } catch (err) {
      console.error('Error loading destash items:', err);
    } finally {
      setLoadingDestash(false);
    }
  };

  // Load Creation watching items
  const loadCreationItems = async (userId: string) => {
    setLoadingCreations(true);
    try {
      const watchingRef = collection(db, 'users', userId, 'watchingCreations');
      const q = query(watchingRef, orderBy('addedAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const items: WatchingCreationItem[] = [];
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const item: WatchingCreationItem = {
          id: docSnap.id,
          creationId: data.creationId,
          creatorId: data.creatorId,
          title: data.title,
          photoUrl: data.photoUrl,
          price: data.price,
          marketplaceCategory: data.marketplaceCategory,
          addedAt: data.addedAt,
        };

        // Fetch current status from creation
        try {
          const creationRef = doc(db, 'users', data.creatorId, 'creations', data.creationId);
          const creationDoc = await getDoc(creationRef);
          
          if (creationDoc.exists()) {
            const creationData = creationDoc.data();
            item.currentStatus = creationData.marketplaceStatus || 'available';
          } else {
            item.currentStatus = 'removed';
          }
        } catch (err) {
          item.currentStatus = 'removed';
        }

        // Fetch creator info
        try {
          const creatorRef = doc(db, 'users', data.creatorId);
          const creatorDoc = await getDoc(creatorRef);
          
          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data();
            item.creatorUsername = creatorData.username || creatorData.profile?.username;
            item.creatorAvatar = creatorData.avatarUrl || creatorData.profile?.avatarUrl;
          }
        } catch (err) {
          console.error('Error fetching creator info:', err);
        }

        items.push(item);
      }

      setCreationItems(items);
    } catch (err) {
      console.error('Error loading creation items:', err);
    } finally {
      setLoadingCreations(false);
    }
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================

  // Mark ISO as found (remove from wishlist)
  const handleFoundISO = async (itemId: string) => {
    if (!currentUserId) return;
    
    setRemovingId(itemId);
    try {
      const itemRef = doc(db, 'users', currentUserId, 'items', itemId);
      await updateDoc(itemRef, {
        onWishlist: false,
        updatedAt: serverTimestamp()
      });
      setIsoItems(prev => prev.filter(item => item.id !== itemId));
    } catch (err) {
      console.error('Error updating item:', err);
    } finally {
      setRemovingId(null);
    }
  };

  // Remove destash from watching
  const handleRemoveDestash = async (watchId: string) => {
    if (!currentUserId) return;
    
    setRemovingId(watchId);
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'marketplaceWishlist', watchId));
      setDestashItems(prev => prev.filter(item => item.id !== watchId));
    } catch (err) {
      console.error('Error removing destash:', err);
    } finally {
      setRemovingId(null);
    }
  };

  // Remove creation from watching
  const handleRemoveCreation = async (watchId: string) => {
    if (!currentUserId) return;
    
    setRemovingId(watchId);
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'watchingCreations', watchId));
      setCreationItems(prev => prev.filter(item => item.id !== watchId));
    } catch (err) {
      console.error('Error removing creation:', err);
    } finally {
      setRemovingId(null);
    }
  };

  // Save item updates to Firebase
  const handleSaveItem = async (itemId: string, updates: Partial<CollectionItem>) => {
    if (!currentUserId) return;
    
    try {
      const itemRef = doc(db, 'users', currentUserId, 'items', itemId);
      await updateDoc(itemRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setIsoItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      ));
      
      // Update selectedItem if it's the same item
      if (selectedItem?.id === itemId) {
        setSelectedItem(prev => prev ? { ...prev, ...updates } : null);
      }
      
      // If onWishlist was set to false, remove from list
      if (updates.onWishlist === false) {
        setIsoItems(prev => prev.filter(item => item.id !== itemId));
        setSelectedItem(null);
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  // Delete item permanently
  const handleDeleteItem = async (itemId: string) => {
    if (!currentUserId) return;
    
    try {
      await deleteDoc(doc(db, 'users', currentUserId, 'items', itemId));
      setIsoItems(prev => prev.filter(item => item.id !== itemId));
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  // Open inquiry modal for destash
  const handleMessageDestashSeller = (item: WatchingDestashItem) => {
    setInquiryRecipientId(item.sellerId);
    setInquiryRecipientUsername(item.sellerUsername || null);
    setInquiryItems([{
      itemId: item.itemId,
      ownerId: item.sellerId,
      name: item.itemName,
      brand: item.itemBrand,
      photoUrl: item.photoUrl,
      status: (item.currentStatus === 'removed' ? 'available' : item.currentStatus) || 'available',
    }]);
    setShowInquiryModal(true);
  };

  // Open inquiry modal for creation
  const handleMessageCreator = (item: WatchingCreationItem) => {
    setInquiryRecipientId(item.creatorId);
    setInquiryRecipientUsername(item.creatorUsername || null);
    setInquiryItems([{
      itemId: item.creationId,
      ownerId: item.creatorId,
      name: item.title,
      brand: 'Creation',
      photoUrl: item.photoUrl,
      status: (item.currentStatus === 'removed' ? 'available' : item.currentStatus) || 'available',
    }]);
    setShowInquiryModal(true);
  };

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Expand/collapse all categories
  const expandAllCategories = () => {
    const allTypes = [...new Set(isoItems.map(item => item.itemType || 'unknown'))];
    setExpandedCategories(new Set(allTypes));
  };

  const collapseAllCategories = () => {
    setExpandedCategories(new Set());
  };

  // Per-category pagination helpers
  const getCategoryPage = (category: string): number => {
    return categoryPages[category] || 1;
  };

  const setCategoryPage = (category: string, page: number) => {
    setCategoryPages(prev => ({ ...prev, [category]: page }));
  };

  const getPaginatedCategoryItems = (category: string, items: ISOItem[]) => {
    const page = getCategoryPage(category);
    const startIndex = (page - 1) * isoItemsPerPage;
    const endIndex = startIndex + isoItemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getCategoryTotalPages = (items: ISOItem[]): number => {
    return Math.ceil(items.length / isoItemsPerPage);
  };

  // Generate page numbers for category pagination
  const getCategoryPageNumbers = (totalPages: number, currentPage: number): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  // ============================================================================
  // HELPERS
  // ============================================================================


  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'available':
        return { bg: 'bg-green-500', text: 'Available' };
      case 'pending':
        return { bg: 'bg-yellow-500', text: 'Pending' };
      case 'sold':
        return { bg: 'bg-red-500', text: 'Sold' };
      case 'removed':
        return { bg: 'bg-gray-500', text: 'No longer listed' };
      default:
        return { bg: 'bg-gray-400', text: 'Unknown' };
    }
  };

  const getPriorityBadge = (priority: string | undefined) => {
    const key = (priority || 'iso') as keyof typeof PriorityColors;
    const colors = PriorityColors[key] || PriorityColors.iso;
    return { bg: colors.bg, text: colors.label };
  };

  const getItemImage = (item: ISOItem): string | null => {
    if (item.photos && item.photos.length > 0) {
      const defaultPhoto = item.photos.find(p => p.isDefault);
      return defaultPhoto ? defaultPhoto.url : item.photos[0].url;
    }
    if (item.photoUrl) return item.photoUrl;
    return null;
  };


  // ============================================================================
  // FILTERED DATA WITH MEMOIZATION
  // ============================================================================

  // Count priorities for badges
  const priorityCounts = useMemo(() => {
    return {
      unicorn: isoItems.filter(i => i.isoPriority === 'unicorn').length,
      diso: isoItems.filter(i => i.isoPriority === 'diso').length,
      iso: isoItems.filter(i => !i.isoPriority || i.isoPriority === 'iso').length,
    };
  }, [isoItems]);

  // Count categories for badges
  const categoryCounts = useMemo(() => {
    return {
      pressons: creationItems.filter(i => i.marketplaceCategory === 'pressons').length,
      jewelry: creationItems.filter(i => i.marketplaceCategory === 'jewelry').length,
      other: creationItems.filter(i => !i.marketplaceCategory || i.marketplaceCategory === 'other').length,
    };
  }, [creationItems]);

  // Filter ISO items
  const filteredISO = useMemo(() => {
    let result = [...isoItems];
    
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(q) || 
        item.brand.toLowerCase().includes(q)
      );
    }
    
    // Priority filter
    if (priorityFilter !== 'all') {
      result = result.filter(item => {
        const itemPriority = item.isoPriority || 'iso';
        return itemPriority === priorityFilter;
      });
    }
    
    return result;
  }, [isoItems, searchQuery, priorityFilter]);

  // Group ISO items by itemType (for collapsible sections)
  const isoItemsByCategory = useMemo(() => {
    const grouped: Record<string, ISOItem[]> = {};
    
    filteredISO.forEach(item => {
      const type = item.itemType || 'unknown';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(item);
    });
    
    // Sort each category's items by priority, then brand, then name
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => {
        const priorityOrder = { unicorn: 0, diso: 1, iso: 2 };
        const aPriority = priorityOrder[a.isoPriority || 'iso'] ?? 2;
        const bPriority = priorityOrder[b.isoPriority || 'iso'] ?? 2;
        if (aPriority !== bPriority) return aPriority - bPriority;
        
        const brandCompare = a.brand.localeCompare(b.brand);
        if (brandCompare !== 0) return brandCompare;
        
        return a.name.localeCompare(b.name);
      });
    });
    
    return grouped;
  }, [filteredISO]);

  // Get sorted category keys (alphabetically by display label)
  const sortedCategoryKeys = useMemo(() => {
    return Object.keys(isoItemsByCategory).sort((a, b) => {
      const labelA = getItemTypeDisplay(a).label;
      const labelB = getItemTypeDisplay(b).label;
      return labelA.localeCompare(labelB);
    });
  }, [isoItemsByCategory]);

  // Filter destash items
  const filteredDestash = useMemo(() => {
    let result = [...destashItems];
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.itemName.toLowerCase().includes(q) || 
        item.itemBrand.toLowerCase().includes(q)
      );
    }
    
    return result;
  }, [destashItems, searchQuery]);

  // Filter creation items
  const filteredCreations = useMemo(() => {
    let result = [...creationItems];
    
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item => item.title.toLowerCase().includes(q));
    }
    
    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter(item => {
        const itemCategory = item.marketplaceCategory || 'other';
        return itemCategory === categoryFilter;
      });
    }
    
    return result;
  }, [creationItems, searchQuery, categoryFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, priorityFilter, categoryFilter, activeTab]);

  // Reset category pages when search or priority filter changes
  useEffect(() => {
    setCategoryPages({});
  }, [searchQuery, priorityFilter, isoItemsPerPage]);

  // Get current items based on active tab
  const getCurrentFilteredItems = () => {
    switch (activeTab) {
      case 'seeking': return filteredISO;
      case 'destash': return filteredDestash;
      case 'creations': return filteredCreations;
      default: return [];
    }
  };

  const currentFilteredItems = getCurrentFilteredItems();
  const totalPages = Math.ceil(currentFilteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Paginated items for destash and creations
  const paginatedDestash = filteredDestash.slice(startIndex, endIndex);
  const paginatedCreations = filteredCreations.slice(startIndex, endIndex);

  // Generate page numbers with ellipsis
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Not logged in
  if (!loading && !currentUserId) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="glass-card p-8 text-center max-w-md">
          <div className="text-6xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold text-glass mb-2 font-heading">
            Sign In Required
          </h2>
          <p className="text-glass-secondary mb-6 font-body">
            Please sign in to view your wants and watched items.
          </p>
          <Link
            to="/auth"
            className="inline-block px-6 py-3 bg-slate-700 text-white rounded-xl font-bold hover:bg-slate-800 transition-default font-body"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-glass mx-auto mb-4" />
          <p className="text-glass-secondary font-body">Loading your wants...</p>
        </div>
      </div>
    );
  }

  const totalCount = isoItems.length + destashItems.length + creationItems.length;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Visibility Banner */}
        {username && (
          <PageVisibilityBanner 
            isPublic={isWishlistPublic}
            publicPath={`/wishlist/${username}`}
            pageType="wishlist"
            className="mb-4"
            onPreview={() => navigate(`/wishlist/${username}`)}
          />
        )}

        {/* Back button above card */}
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-on-bg transition-default"
          aria-label="Go back"
        >
          <ArrowLeft className="icon-md" />
          <span className="font-body">Back</span>
        </button>

        {/* HEADER */}
        <PageHeader
          icon={Star}
          title="My Wishlist & Watched Items"
          subtitle={`${totalCount} item${totalCount !== 1 ? 's' : ''} total`}
          className="mb-6"
        />

        {/* Search Bar - GLASSMORPHIC */}
        <div className="glass-card p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your wants..."
              className="w-full pl-10 pr-10 py-2.5 bg-slate-800/60 border-0 rounded-xl focus:ring-2 focus:ring-slate-500 text-glass placeholder:text-glass-muted font-body"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-700 rounded-full transition-default"
              >
                <X className="icon-sm text-glass-muted" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs - GLASSMORPHIC */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setActiveTab('seeking'); setPriorityFilter('all'); setCategoryFilter('all'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium font-body transition-all ${
                activeTab === 'seeking'
                  ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-lg'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
              }`}
            >
              <Sparkles className="icon-sm" />
              Items I'm Seeking
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'seeking' ? 'bg-white/30 text-white' : 'bg-slate-700 text-white'
              }`}>
                {isoItems.length}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('destash'); setPriorityFilter('all'); setCategoryFilter('all'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium font-body transition-all ${
                activeTab === 'destash'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
              }`}
            >
              <Star className="icon-sm" />
              Destash I'm Watching
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'destash' ? 'bg-white/30 text-white' : 'bg-slate-700 text-white'
              }`}>
                {destashItems.length}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('creations'); setPriorityFilter('all'); setCategoryFilter('all'); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium font-body transition-all ${
                activeTab === 'creations'
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg'
                  : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
              }`}
            >
              <Star className="icon-sm" />
              Creations I'm Watching
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === 'creations' ? 'bg-white/30 text-white' : 'bg-slate-700 text-white'
              }`}>
                {creationItems.length}
              </span>
            </button>
          </div>
        </div>

        {/* ================================================================ */}
        {/* ITEMS I'M SEEKING (ISOs) - WITH COLLAPSIBLE CATEGORIES */}
        {/* ================================================================ */}
        {activeTab === 'seeking' && (
          <div>
            {/* Priority Filters */}
            <div className="glass-card p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setPriorityFilter('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                      priorityFilter === 'all'
                        ? 'bg-slate-700 text-white'
                        : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                    }`}
                  >
                    All ({isoItems.length})
                  </button>
                  <button
                    onClick={() => setPriorityFilter('unicorn')}
                    className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                      priorityFilter === 'unicorn'
                        ? `${PriorityColors.unicorn.bg} text-white`
                        : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                    }`}
                  >
                    {PriorityColors.unicorn.label} ({priorityCounts.unicorn})
                  </button>
                  <button
                    onClick={() => setPriorityFilter('diso')}
                    className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                      priorityFilter === 'diso'
                        ? `${PriorityColors.diso.bg} text-white`
                        : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                    }`}
                  >
                    {PriorityColors.diso.label} ({priorityCounts.diso})
                  </button>
                  <button
                    onClick={() => setPriorityFilter('iso')}
                    className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                      priorityFilter === 'iso'
                        ? `${PriorityColors.iso.bg} text-white`
                        : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                    }`}
                  >
                    {PriorityColors.iso.label} ({priorityCounts.iso})
                  </button>
                </div>
                
                {/* Items per page + Expand/Collapse All */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-glass-muted">Per section:</span>
                    {[10, 20, 50].map((count) => (
                      <button
                        key={count}
                        onClick={() => setIsoItemsPerPage(count)}
                        className={`px-2 py-1 rounded-lg text-xs font-medium transition-default ${
                          isoItemsPerPage === count
                            ? 'bg-slate-700 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                  
                  {/* Expand/Collapse buttons */}
                  {sortedCategoryKeys.length > 1 && (
                    <div className="flex gap-2">
                      <button
                        onClick={expandAllCategories}
                        className="px-3 py-1.5 text-xs font-medium text-glass-secondary bg-slate-800/60 hover:bg-white/80 rounded-lg transition-default font-body"
                      >
                        Expand All
                      </button>
                      <button
                        onClick={collapseAllCategories}
                        className="px-3 py-1.5 text-xs font-medium text-glass-secondary bg-slate-800/60 hover:bg-white/80 rounded-lg transition-default font-body"
                      >
                        Collapse All
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {loadingISO ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-glass" />
              </div>
            ) : filteredISO.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <div className="text-6xl mb-4">🦄</div>
                <h3 className="text-xl font-bold text-glass-secondary mb-2 font-heading">
                  {searchQuery || priorityFilter !== 'all' ? 'No matching items' : 'No items on your seek list'}
                </h3>
                <p className="text-glass-muted mb-4 font-body">
                  {searchQuery || priorityFilter !== 'all'
                    ? 'Try a different search or filter'
                    : 'Add items to your wishlist to track what you\'re hunting for!'
                  }
                </p>
                {!searchQuery && priorityFilter === 'all' && (
                  <Link
                    to="/collection"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-800 transition-default font-body"
                  >
                    <Sparkles className="icon-sm" />
                    Browse Collection
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Collapsible Category Sections */}
                {sortedCategoryKeys.map((categoryKey) => {
                  const items = isoItemsByCategory[categoryKey];
                  const { label, emoji } = getItemTypeDisplay(categoryKey);
                  const isExpanded = expandedCategories.has(categoryKey);
                  
                  // Pagination for this category
                  const categoryCurrentPage = getCategoryPage(categoryKey);
                  const categoryTotalPages = getCategoryTotalPages(items);
                  const paginatedItems = getPaginatedCategoryItems(categoryKey, items);
                  const categoryStartIndex = (categoryCurrentPage - 1) * isoItemsPerPage;
                  const categoryEndIndex = Math.min(categoryStartIndex + isoItemsPerPage, items.length);
                  
                  return (
                    <div
                      key={categoryKey}
                      className="glass-card overflow-hidden"
                    >
                      {/* Category Header - Clickable to expand/collapse */}
                      <button
                        onClick={() => toggleCategory(categoryKey)}
                        className="w-full flex items-center justify-between p-4 hover:bg-white/20 transition-default"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{emoji}</span>
                          <h3 className="text-lg font-bold text-glass font-heading">
                            {label}
                          </h3>
                          <span className="px-2 py-0.5 bg-slate-700 text-glass rounded-full text-sm font-medium">
                            {items.length}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="icon-md text-glass-muted" />
                        ) : (
                          <ChevronDown className="icon-md text-glass-muted" />
                        )}
                      </button>
                      
                      {/* Category Items - Collapsible */}
                      {isExpanded && (
                        <div className="p-4 pt-0">
                          {/* Showing X-Y of Z */}
                          {items.length > isoItemsPerPage && (
                            <p className="text-xs text-glass-muted mb-3 font-body">
                              Showing {categoryStartIndex + 1}-{categoryEndIndex} of {items.length}
                            </p>
                          )}
                          
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {paginatedItems.map((item) => {
                              const priorityBadge = getPriorityBadge(item.isoPriority);
                              const imageUrl = getItemImage(item);

                              return (
                                <div
                                  key={item.id}
                                  onClick={() => setSelectedItem(item)}
                                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
                                >
                                  {/* Photo */}
                                  <div className="relative aspect-square overflow-hidden">
                                    {imageUrl ? (
                                      <img
                                        src={imageUrl}
                                        alt={`${item.brand} ${item.name}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center text-4xl">
                                        💝
                                      </div>
                                    )}

                                    {/* Priority Badge */}
                                    <span className={`absolute top-2 left-2 px-2 py-1 ${priorityBadge.bg} text-white rounded-full text-xs font-bold shadow`}>
                                      {priorityBadge.text}
                                    </span>

                                    {/* Found It Button */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleFoundISO(item.id);
                                      }}
                                      disabled={removingId === item.id}
                                      className="absolute bottom-2 right-2 px-3 py-1.5 bg-white/90 rounded-full shadow-lg hover:bg-green-50 hover:text-green-600 transition-default flex items-center gap-1 text-sm font-medium"
                                      title="I found this item!"
                                    >
                                      {removingId === item.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <>
                                          <Check className="icon-sm" />
                                          Found it!
                                        </>
                                      )}
                                    </button>
                                  </div>

                                  {/* Info */}
                                  <div className="p-3">
                                    <p className="text-xs text-gray-500 font-bold truncate font-heading">
                                      {item.brand}
                                    </p>
                                    <p className="font-bold text-gray-900 truncate text-sm font-body">
                                      {item.name}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Category Pagination Controls */}
                          {categoryTotalPages > 1 && (
                            <div className="flex flex-wrap items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-600">
                              <button
                                onClick={() => setCategoryPage(categoryKey, Math.max(1, categoryCurrentPage - 1))}
                                disabled={categoryCurrentPage === 1}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium font-body transition-default ${
                                  categoryCurrentPage === 1
                                    ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                                    : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                                }`}
                              >
                                ← Prev
                              </button>

                              {getCategoryPageNumbers(categoryTotalPages, categoryCurrentPage).map((page, index) => (
                                page === '...' ? (
                                  <span key={`ellipsis-${categoryKey}-${index}`} className="px-2 text-glass-muted">...</span>
                                ) : (
                                  <button
                                    key={`${categoryKey}-${page}`}
                                    onClick={() => setCategoryPage(categoryKey, page as number)}
                                    className={`w-8 h-8 rounded-lg text-sm font-medium font-body transition-default ${
                                      categoryCurrentPage === page
                                        ? 'bg-slate-700 text-white'
                                        : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                                    }`}
                                  >
                                    {page}
                                  </button>
                                )
                              ))}

                              <button
                                onClick={() => setCategoryPage(categoryKey, Math.min(categoryTotalPages, categoryCurrentPage + 1))}
                                disabled={categoryCurrentPage === categoryTotalPages}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium font-body transition-default ${
                                  categoryCurrentPage === categoryTotalPages
                                    ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                                    : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                                }`}
                              >
                                Next →
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* DESTASH I'M WATCHING */}
        {/* ================================================================ */}
        {activeTab === 'destash' && (
          <div>
            {loadingDestash ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : filteredDestash.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <div className="text-6xl mb-4">👀</div>
                <h3 className="text-xl font-bold text-glass-secondary mb-2 font-heading">
                  {searchQuery ? 'No matching items' : 'Not watching any destash items'}
                </h3>
                <p className="text-glass-muted mb-4 font-body">
                  {searchQuery
                    ? 'Try a different search'
                    : 'Browse the marketplace and add items to your watch list!'
                  }
                </p>
                {!searchQuery && (
                  <Link
                    to="/marketplace"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-default font-body"
                  >
                    <Package className="icon-sm" />
                    Browse Marketplace
                  </Link>
                )}
              </div>
            ) : (
              <>
                {/* Results count and per-page */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <p className="text-sm text-glass-secondary font-body">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredDestash.length)} of {filteredDestash.length} items
                  </p>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-glass-muted">Show:</span>
                    {[25, 50, 100].map((count) => (
                      <button
                        key={count}
                        onClick={() => { setItemsPerPage(count); setCurrentPage(1); }}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-default ${
                          itemsPerPage === count
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {paginatedDestash.map((item) => {
                    const statusBadge = getStatusBadge(item.currentStatus);
                    const isUnavailable = item.currentStatus === 'sold' || item.currentStatus === 'removed';

                    return (
                      <div
                        key={item.id}
                        className={`bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all group ${
                          isUnavailable ? 'opacity-60' : ''
                        }`}
                      >
                        {/* Photo */}
                        <div className="relative aspect-square overflow-hidden">
                          {item.photoUrl ? (
                            <img
                              src={item.photoUrl}
                              alt={`${item.itemBrand} ${item.itemName}`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center text-4xl">
                              📦
                            </div>
                          )}

                          {/* Status Badge */}
                          <span className={`absolute top-2 left-2 px-2 py-1 ${statusBadge.bg} text-white rounded-full text-xs font-bold shadow`}>
                            {statusBadge.text}
                          </span>

                          {/* Swaps Badge */}
                          {item.isOpenToSwaps && (
                            <span className="absolute top-2 right-2 px-2 py-1 bg-purple-500 text-white rounded-full text-xs font-bold shadow flex items-center gap-1">
                              <Repeat className="icon-xs" />
                            </span>
                          )}

                          {/* Sold Overlay */}
                          {item.currentStatus === 'sold' && (
                            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                              <span className="bg-red-500 text-white px-3 py-1 rounded-full font-bold text-sm transform -rotate-12">
                                SOLD
                              </span>
                            </div>
                          )}

                          {/* Removed Overlay */}
                          {item.currentStatus === 'removed' && (
                            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                              <AlertCircle className="icon-lg text-white" />
                            </div>
                          )}

                          {/* Remove Button */}
                          <button
                            onClick={() => handleRemoveDestash(item.id)}
                            disabled={removingId === item.id}
                            className="absolute bottom-2 right-2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-red-50 hover:text-red-500 transition-default opacity-0 group-hover:opacity-100"
                            title="Stop watching"
                          >
                            {removingId === item.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </div>

                        {/* Info */}
                        <div className="p-3">
                          <p className="text-xs text-amber-600 font-bold truncate font-heading">
                            {item.itemBrand}
                          </p>
                          <p className="font-bold text-gray-900 truncate text-sm font-body">
                            {item.itemName}
                          </p>

                          {/* Price */}
                          {(item.currentPrice || item.price) && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold mt-1 ${
                              isUnavailable ? 'bg-gray-200 text-gray-500 line-through' : 'bg-green-100 text-green-800'
                            }`}>
                              <Tag className="icon-xs" />
                              ${item.currentPrice || item.price}
                            </span>
                          )}

                          {/* Seller */}
                          {item.sellerUsername && (
                            <Link
                              to={`/profile/${item.sellerUsername}`}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-500 transition-default mt-2"
                            >
                              @{item.sellerUsername}
                              <ExternalLink className="icon-xs" />
                            </Link>
                          )}

                          {/* Message Button */}
                          {!isUnavailable && (
                            <button
                              onClick={() => handleMessageDestashSeller(item)}
                              className="mt-2 w-full py-1.5 px-2 bg-amber-500 text-white rounded-lg font-bold text-xs hover:bg-amber-600 transition-default flex items-center justify-center gap-1"
                            >
                              <MessageSquare className="icon-xs" />
                              Message
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-xl font-medium font-body transition-default ${
                        currentPage === 1
                          ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                      }`}
                    >
                      ← Prev
                    </button>

                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-glass-muted">...</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page as number)}
                          className={`w-10 h-10 rounded-xl font-medium font-body transition-default ${
                            currentPage === page
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-xl font-medium font-body transition-default ${
                        currentPage === totalPages
                          ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                      }`}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* CREATIONS I'M WATCHING */}
        {/* ================================================================ */}
        {activeTab === 'creations' && (
          <div>
            {/* Category Filters */}
            <div className="glass-card p-4 mb-6">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCategoryFilter('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                    categoryFilter === 'all'
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                  }`}
                >
                  All ({creationItems.length})
                </button>
                <button
                  onClick={() => setCategoryFilter('pressons')}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                    categoryFilter === 'pressons'
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                  }`}
                >
                  💅 Press-Ons ({categoryCounts.pressons})
                </button>
                <button
                  onClick={() => setCategoryFilter('jewelry')}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                    categoryFilter === 'jewelry'
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                  }`}
                >
                  💍 Jewelry ({categoryCounts.jewelry})
                </button>
                <button
                  onClick={() => setCategoryFilter('other')}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-default ${
                    categoryFilter === 'other'
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                  }`}
                >
                  ✨ Other ({categoryCounts.other})
                </button>
              </div>
            </div>

            {loadingCreations ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              </div>
            ) : filteredCreations.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <div className="text-6xl mb-4">✨</div>
                <h3 className="text-xl font-bold text-glass-secondary mb-2 font-heading">
                  {searchQuery || categoryFilter !== 'all' ? 'No matching creations' : 'Not watching any creations'}
                </h3>
                <p className="text-glass-muted mb-4 font-body">
                  {searchQuery || categoryFilter !== 'all'
                    ? 'Try a different search or filter'
                    : 'Discover handmade nail art creations from talented artists!'
                  }
                </p>
                {!searchQuery && categoryFilter === 'all' && (
                  <Link
                    to="/creations"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-default font-body"
                  >
                    <Sparkles className="icon-sm" />
                    Browse Creations
                  </Link>
                )}
              </div>
            ) : (
              <>
                {/* Results count and per-page */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <p className="text-sm text-glass-secondary font-body">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredCreations.length)} of {filteredCreations.length} creations
                  </p>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-glass-muted">Show:</span>
                    {[25, 50, 100].map((count) => (
                      <button
                        key={count}
                        onClick={() => { setItemsPerPage(count); setCurrentPage(1); }}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-default ${
                          itemsPerPage === count
                            ? 'bg-purple-500 text-white'
                            : 'bg-slate-800/60 text-glass-secondary hover:bg-white/80'
                        }`}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {paginatedCreations.map((item) => {
                    const statusBadge = getStatusBadge(item.currentStatus);
                    const isUnavailable = item.currentStatus === 'sold' || item.currentStatus === 'removed';

                    return (
                      <div
                        key={item.id}
                        className={`bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all group ${
                          isUnavailable ? 'opacity-60' : ''
                        }`}
                      >
                        {/* Photo */}
                        <div className="relative aspect-square overflow-hidden">
                          {item.photoUrl ? (
                            <img
                              src={item.photoUrl}
                              alt={item.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center text-4xl">
                              ✨
                            </div>
                          )}

                          {/* Status Badge */}
                          <span className={`absolute top-2 left-2 px-2 py-1 ${statusBadge.bg} text-white rounded-full text-xs font-bold shadow`}>
                            {statusBadge.text}
                          </span>

                          {/* Category Badge */}
                          {item.marketplaceCategory && (
                            <span className="absolute top-2 right-2 px-2 py-1 bg-purple-500 text-white rounded-full text-xs font-bold shadow">
                              {item.marketplaceCategory === 'pressons' ? '💅' : 
                               item.marketplaceCategory === 'jewelry' ? '💍' : '✨'}
                            </span>
                          )}

                          {/* Sold Overlay */}
                          {item.currentStatus === 'sold' && (
                            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                              <span className="bg-red-500 text-white px-3 py-1 rounded-full font-bold text-sm transform -rotate-12">
                                SOLD
                              </span>
                            </div>
                          )}

                          {/* Removed Overlay */}
                          {item.currentStatus === 'removed' && (
                            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                              <AlertCircle className="icon-lg text-white" />
                            </div>
                          )}

                          {/* Remove Button */}
                          <button
                            onClick={() => handleRemoveCreation(item.id)}
                            disabled={removingId === item.id}
                            className="absolute bottom-2 right-2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-red-50 hover:text-red-500 transition-default opacity-0 group-hover:opacity-100"
                            title="Stop watching"
                          >
                            {removingId === item.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                            ) : (
                              <Trash2 className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </div>

                        {/* Info */}
                        <div className="p-3">
                          <p className="font-bold text-gray-900 truncate text-sm font-body">
                            {item.title}
                          </p>

                          {/* Price */}
                          {item.price && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold mt-1 ${
                              isUnavailable ? 'bg-gray-200 text-gray-500 line-through' : 'bg-green-100 text-green-800'
                            }`}>
                              <Tag className="icon-xs" />
                              ${item.price}
                            </span>
                          )}

                          {/* Creator */}
                          {item.creatorUsername && (
                            <Link
                              to={`/profile/${item.creatorUsername}`}
                              className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-500 transition-default mt-2"
                            >
                              @{item.creatorUsername}
                              <ExternalLink className="icon-xs" />
                            </Link>
                          )}

                          {/* Message Button */}
                          {!isUnavailable && (
                            <button
                              onClick={() => handleMessageCreator(item)}
                              className="mt-2 w-full py-1.5 px-2 bg-purple-500 text-white rounded-lg font-bold text-xs hover:bg-purple-600 transition-default flex items-center justify-center gap-1"
                            >
                              <MessageSquare className="icon-xs" />
                              Message
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-xl font-medium font-body transition-default ${
                        currentPage === 1
                          ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                      }`}
                    >
                      ← Prev
                    </button>

                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className="px-2 text-glass-muted">...</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page as number)}
                          className={`w-10 h-10 rounded-xl font-medium font-body transition-default ${
                            currentPage === page
                              ? 'bg-purple-500 text-white'
                              : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    ))}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-xl font-medium font-body transition-default ${
                        currentPage === totalPages
                          ? 'bg-white/30 text-glass-muted cursor-not-allowed'
                          : 'bg-slate-800/60 text-glass-secondary hover:bg-slate-700'
                      }`}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Inquiry Modal */}
      {currentUserId && inquiryRecipientId && (
        <InquiryModal
          isOpen={showInquiryModal}
          onClose={() => {
            setShowInquiryModal(false);
            setInquiryRecipientId(null);
            setInquiryRecipientUsername(null);
            setInquiryItems([]);
          }}
          currentUserId={currentUserId}
          sellerId={inquiryRecipientId}
          sellerUsername={inquiryRecipientUsername}
          items={inquiryItems}
        />
      )}

      {/* Item Detail Modals */}
      {selectedItem && selectedItem.itemType === 'polish' && (
        <PolishDetailModal
          polish={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleSaveItem}
          onDelete={handleDeleteItem}
        />
      )}

      {selectedItem && selectedItem.itemType === 'plate' && (
        <PlateDetailModal
          plate={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleSaveItem}
          onDelete={handleDeleteItem}
        />
      )}

      {selectedItem && selectedItem.itemType === 'transfer' && (
        <DecalDetailModal
          decal={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleSaveItem}
          onDelete={handleDeleteItem}
        />
      )}

      {selectedItem && selectedItem.itemType === 'embellishment' && (
        <EmbellishmentDetailModal
          embellishment={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleSaveItem}
          onDelete={handleDeleteItem}
        />
      )}

      {selectedItem && selectedItem.itemType === 'tool' && (
        <ToolDetailModal
          tool={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleSaveItem}
          onDelete={handleDeleteItem}
        />
      )}

      {selectedItem && selectedItem.itemType === 'powder' && (
        <BuffingPowderDetailModal
          powder={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleSaveItem}
          onDelete={handleDeleteItem}
        />
      )}

      {selectedItem && selectedItem.itemType === 'acrylic' && (
        <AcrylicDetailModal
          acrylic={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={handleSaveItem}
          onDelete={handleDeleteItem}
        />
      )}
    </div>
  );
};

export default Wishlist;
